import { isEditLeaseLost } from '../../utils/functionMutation';
import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, Typography, Box, CircularProgress,
  Button, List, ListItem, ListItemIcon, ListItemText, DialogActions, Alert, Checkbox, Stack
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { authenticatedFetch } from '../../services/api_utils';
import { functionEndpoint } from '../../services/endpoints';
import {
  classifyAmbiguousPublishEnd,
  isPublishedVersion,
  PUBLISH_ACTOR,
  loadFunctionVersion,
  readFunctionHead,
  versionDataSourceBlocks,
  versionEmailOutputs,
} from '../../services/functionVersions';
import {
  automationConfigSnapshot,
  describeWhen,
  keepAutomationsRunning,
  migrateTriggerParameters,
} from '../../services/functionTriggers';
import { list as listContacts } from '../../services/emailContacts';
import { recipientEntry } from '../../utils/emailConfirmation';
import { formatText } from '../common/TextFormatterProvider';

// Publishing is the single safety choke point. It validates the draft end to
// end, snapshots it into an immutable `versions/{n}` document, advances the head
// pointer, and — because a new version changes what the Function does — switches
// off every user-created Automation of this Function, all in one backend
// transaction. Runs execute published versions only, so this dialog is the only
// way an edit ever reaches production.
//
// Republishing unchanged content is deduplicated by content hash: it returns
// `unchanged: true`, mints no version, and sweeps no Automation.
//
// `swept_automations` lists exactly the Automations that went from running to
// off, each with the sweep receipt that authorises this prompt to put it back.
// The prompt is a review, not a formality: an Automation whose saved filters no
// longer apply to the new version comes back UNCHECKED and never has its Email
// permission restored, because an unfiltered scheduled report reaching a
// recipient list is the exact defect this flow exists to prevent.

const STALE_MESSAGES = {
  version_advanced: 'This function was published again before you answered, so nothing was written. Turn these automations back on from the Automations tab.',
  version_missing: 'The version this publish produced is no longer available, so nothing was written. Review these automations in the Automations tab.',
  function_invalid: 'This function now references a resource that no longer exists, so nothing was written. Review these automations in the Automations tab.',
  automation_changed: 'One of these automations changed since this publish, so nothing was written. Turn them back on from the Automations tab.',
  function_missing: 'This function no longer exists.',
  old_version_missing: 'The version one of these automations ran on could not be read, so nothing was written. Turn them back on from the Automations tab.',
  table_orphaned: 'This version no longer reads a table one of these automations watches. Nothing was written. Review it in the Automations tab.',
  email_review_changed: 'The saved filters no longer match this Email review, so nothing was written. Review these automations in the Automations tab.',
};

// A swept row the backend flagged, or one that arrived without the identity this
// prompt needs, is shown as a warning and cannot be selected.
const isOfferable = (row) => (
  Boolean(row?.id) && Boolean(row?.sweep_token) && row?.orphaned !== true
);

function PublishFunctionDialog({
  open,
  onClose,
  companyName,
  functionId,
  actorUid,
  sessionId,
  onPublished,
  onEditLeaseLost,
}) {
  // The builder session the backend claims the edit lease for while it
  // validates and publishes (H97): the builder's own when it opens this
  // dialog; otherwise one minted for this dialog, which the backend gives back
  // afterwards — the Dashboard's first-publish naming flow opens it after the
  // builder has closed and released its lease.
  const sessionIdRef = useRef(sessionId || crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  // The stream died and the head re-read could not settle what happened.
  const [uncertain, setUncertain] = useState(false);
  // Set once this dialog published (including an unchanged republish), so
  // closing it can end the editing session rather than return to the canvas.
  const [published, setPublished] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  // Post-publish prompt offering to keep the Automations this publish switched
  // off running against the new version.
  const [keepPrompt, setKeepPrompt] = useState(null);
  const [keepSelection, setKeepSelection] = useState({});
  const [keepBusy, setKeepBusy] = useState(false);
  const [keepError, setKeepError] = useState('');

  const prePublishHeadRef = useRef(null);
  const publishRefusedRef = useRef(false);
  const [checkingPublish, setCheckingPublish] = useState(false);

  // What a lost verdict actually did, decided from the head. The backend
  // commits the publish transaction before it streams the terminal event, so
  // neither a dropped stream nor an error response proves nothing changed
  // (H48, M232): only the head can say.
  const settleFromHead = async () => {
    setCheckingPublish(true);
    let postHead = null;
    try {
      postHead = await readFunctionHead(companyName, functionId, { fromServer: true });
    } catch (err) {
      console.error('Failed to re-read the function head after an interrupted publish:', err);
    }
    const ending = classifyAmbiguousPublishEnd(prePublishHeadRef.current, postHead);
    setCheckingPublish(false);
    setHasError(false);
    setUncertain(false);
    if (ending.kind === 'published') {
      setMessages(prev => [...prev, {
        text: `The publish completed — version ${ending.publishedVersion} is live. `
          + 'Automations switched off by this publish can be turned back on from the Automations tab.',
        type: 'success',
      }]);
      setPublished(true);
      if (typeof onPublished === 'function') {
        onPublished({ publishedVersion: ending.publishedVersion, unchanged: false, sweptAutomations: [] });
      }
    } else if (ending.kind === 'unchanged') {
      setMessages(prev => [...prev, {
        text: `No changes to publish — this function still runs version ${ending.publishedVersion}.`,
        type: 'success',
      }]);
      setPublished(true);
      if (typeof onPublished === 'function') {
        onPublished({ publishedVersion: ending.publishedVersion, unchanged: true, sweptAutomations: [] });
      }
    } else if (publishRefusedRef.current) {
      setMessages(prev => [...prev, { text: 'Publishing failed. Nothing was published.', type: 'error' }]);
      setHasError(true);
    } else {
      setMessages(prev => [...prev, {
        text: 'The result could not be confirmed. Check the published version and the Automations tab before retrying.',
        type: 'error',
      }]);
      setUncertain(true);
    }
  };

  const handleStartPublish = async () => {
    setIsPublishing(true);
    setMessages([]);
    setHasError(false);
    setUncertain(false);
    setPublished(false);
    setIsComplete(false);

    // Local flags: React state set inside the read loop is not visible to this
    // closure, so the verdict must never be derived from state reads here.
    let sawError = false;
    let doneEvent = null;
    let successfulPublishHandled = false;
    publishRefusedRef.current = false;
    prePublishHeadRef.current = null;

    // The head as it stood before this attempt: the reference point for
    // deciding what a dropped stream actually did (H48).
    try {
      prePublishHeadRef.current = await readFunctionHead(companyName, functionId, { fromServer: true });
    } catch (err) {
      console.error('Failed to read the function head before publishing:', err);
    }

    try {
      const response = await authenticatedFetch(
        functionEndpoint('validate_data_function'), {
          method: 'POST',
          body: JSON.stringify({ function_id: functionId, publish: true, session_id: sessionIdRef.current }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Publish request failed (${response.status}): ${text}`);
      }
      if (!response.body) {
          throw new Error("The response from the server is empty.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!doneEvent) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data:')) {
            try {
              const data = JSON.parse(line.substring(5));
              if (data.type === 'status') {
                setMessages(prev => [...prev, { text: data.content, type: 'status' }]);
              } else if (data.type === 'error') {
                sawError = true;
                if (isEditLeaseLost(data)) onEditLeaseLost?.();
                setMessages(prev => [...prev, { text: data.content, type: 'error' }]);
                setHasError(true);
              } else if (data.type === 'done') {
                doneEvent = data;
                publishRefusedRef.current = data.valid === false && data.publish_outcome === 'not_published';
              }
            } catch (e) {
              console.error("Error parsing stream data:", e);
              sawError = true;
              setMessages(prev => [...prev, { text: `Error parsing stream: ${line}`, type: 'error' }]);
              setHasError(true);
            }
          }
        }
      }

      const publishedVersion = doneEvent?.published_version;
      if (doneEvent?.valid === true && isPublishedVersion(publishedVersion) && !sawError) {
        setMessages(prev => [...prev, {
          text: doneEvent.unchanged === true
            ? `No changes to publish — this function still runs version ${publishedVersion}.`
            : `Published version ${publishedVersion}.`,
          type: 'success',
        }]);
        const result = {
          publishedVersion,
          unchanged: doneEvent.unchanged === true,
          sweptAutomations: Array.isArray(doneEvent.swept_automations) ? doneEvent.swept_automations : [],
        };
        successfulPublishHandled = true;
        setPublished(true);
        if (typeof onPublished === 'function') onPublished(result);
        await maybePromptKeep(result);
      } else {
        setMessages(prev => [...prev, {
          text: 'Checking what was published…',
          type: 'status',
        }]);
        await settleFromHead();
      }
    } catch (err) {
      if (successfulPublishHandled) {
        // The backend's verdict arrived and was handled above; this failure is
        // in the follow-up (the keep prompt), not in the publish.
        setMessages(prev => [...prev, { text: `Something went wrong after publishing: ${err.message}`, type: 'error' }]);
      } else {
        // An error response, a fetch that threw, or a stream reader that died
        // mid-way. The request may still have reached the backend and
        // committed, so this is as ambiguous as a dropped stream (M232).
        setMessages(prev => [...prev, {
          text: `Publishing did not return a result (${err.message}). Checking what was published…`,
          type: 'status',
        }]);
        await settleFromHead();
      }
    } finally {
        setIsComplete(true);
    }
  };

  // The data-source blocks of the version an automation last ran on, for the
  // preview's filter comparison. `{ blocks }` when read; `{ unreadable: true }`
  // when the pin or its version cannot be read — that row is then shown as
  // needing attention rather than as clean, because a comparison that could
  // not be made must not pass for one that found nothing wrong. The
  // authoritative comparison happens again inside keepAutomationsRunning.
  const loadPreviousBlocks = async (row, newVersion, cache) => {
    try {
      const snapshot = await getDoc(doc(db, `users/${companyName}/function_triggers`, row.id));
      const trigger = snapshot.exists() ? snapshot.data() : null;
      if (!trigger || trigger.function_id !== functionId || trigger.managed_by
        || trigger.enabled !== false || trigger.allow_email === true
        || trigger.email_permission_updated_by !== PUBLISH_ACTOR
        || trigger.publish_sweep?.token !== row.sweep_token
        || trigger.publish_sweep?.version !== newVersion) {
        return { changed: true };
      }
      const pin = trigger.function_version;
      if (!isPublishedVersion(pin) || pin === newVersion) return { unreadable: true };
      if (!Object.prototype.hasOwnProperty.call(cache, pin)) {
        const content = await loadFunctionVersion(companyName, functionId, pin);
        cache[pin] = content ? versionDataSourceBlocks(content) : null;
      }
      return cache[pin] ? { blocks: cache[pin], trigger } : { unreadable: true };
    } catch (err) {
      console.error('Failed to read the previously pinned version of an automation:', err);
      return { unreadable: true };
    }
  };

  // Offer the keep prompt only when this publish actually switched something off.
  const maybePromptKeep = async (result) => {
    if (result.sweptAutomations.length === 0) return;

    let content = null;
    try {
      content = await loadFunctionVersion(companyName, functionId, result.publishedVersion);
    } catch (err) {
      console.error('Failed to load the published version for the keep prompt:', err);
    }
    // Without the version's own contract there is no honest preview and no
    // parameters to migrate onto, so the prompt is not offered at all: the
    // automations stay off and are re-enabled one at a time from the tab.
    if (!content) {
      setMessages(prev => [...prev, {
        text: `${result.sweptAutomations.length} automation${result.sweptAutomations.length === 1 ? ' was' : 's were'} switched off by this publish. Turn them back on from the Automations tab.`,
        type: 'status',
      }]);
      return;
    }
    const newBlocks = versionDataSourceBlocks(content);
    const emailOutputs = versionEmailOutputs(content);

    // Recipients are named from the NEW version, because keeping an Automation
    // running is what would send to them.
    // Email comes back only against recipients this prompt can name, address
    // and all. A contact read that fails leaves Email off for every row,
    // re-allowable from the Automations tab, rather than restored against
    // destinations nobody saw (H49).
    let contacts = [];
    let emailRestorable = true;
    if (emailOutputs.length > 0 && result.sweptAutomations.some((row) => row.had_email === true)) {
      try {
        contacts = await listContacts(companyName);
      } catch (err) {
        console.error('Failed to load contacts for the keep prompt:', err);
        emailRestorable = false;
      }
    }
    const contactsById = new Map();
    contacts.forEach((contact) => contactsById.set(contact.id || contact.contact_id, contact));
    // Every recipient as the contact book has it: "Name <address>", or the
    // reason it cannot be emailed — suppressed, deleted, still being restored,
    // or not in the contact book at all (utils/emailConfirmation, the check
    // every other Email consent uses). Email is restored only when every
    // recipient can be emailed.
    const recipients = emailOutputs.flatMap((output) => (
      output.recipientContactIds.map((id) => recipientEntry(id, contactsById.get(id)))
    ));
    const recipientNames = Array.from(new Set(recipients.map((recipient) => recipient.label)));
    const blockedRecipients = recipients.filter((recipient) => recipient.blocked);
    if (blockedRecipients.length > 0) {
      emailRestorable = false;
    }
    if (emailOutputs.some((output) => output.recipientContactIds.length === 0)) {
      emailRestorable = false;
    }
    // A blocked recipient the contact book holds is named with its reason. One
    // it does not hold may only mean the contacts could not be read, which the
    // prompt already words as "could not be read".
    const blockedRecipientsSentence = blockedRecipients
      .filter((recipient) => contactsById.has(recipient.id))
      .map((recipient) => `${recipient.label}: ${recipient.blocked}`)
      .join(' ');

    const cache = {};
    const rows = [];
    for (const swept of result.sweptAutomations) {
      const previous = isOfferable(swept)
        ? await loadPreviousBlocks(swept, result.publishedVersion, cache)
        : { unreadable: true };
      const offerable = isOfferable(swept) && !previous.unreadable && !previous.changed;
      const { dropped } = offerable
        ? migrateTriggerParameters(previous.trigger.parameters, newBlocks, previous.blocks)
        : { dropped: [] };
      rows.push({
        id: swept.id,
        name: swept.name || swept.id,
        sweepToken: swept.sweep_token,
        expectedConfig: previous.trigger ? automationConfigSnapshot(previous.trigger) : null,
        offerable,
        changed: Boolean(previous.changed),
        orphaned: swept.orphaned === true,
        unreadable: isOfferable(swept) && Boolean(previous.unreadable),
        hadEmail: swept.had_email === true,
        canRestoreEmail: offerable && swept.had_email === true && dropped.length === 0
          && emailOutputs.length > 0 && emailRestorable,
        droppedCount: dropped.length,
        when: describeWhen(
          {
            trigger_type: swept.trigger_type,
            on_schedule_config: swept.on_schedule_config,
            on_new_data_config: { table_id: swept.on_new_data_table_id },
          },
          swept.on_new_data_table_id
            ? { [swept.on_new_data_table_id]: formatText(swept.on_new_data_table_name || swept.on_new_data_table_id) }
            : {},
        ),
        // Absent on a malformed row, which is warned about without claiming to
        // know what it watched.
        tableLabel: swept.on_new_data_table_name || swept.on_new_data_table_id
          ? formatText(swept.on_new_data_table_name || swept.on_new_data_table_id)
          : null,
      });
    }

    // A clean row is one this version still understands, so it comes back
    // checked; a row that lost saved filters must be reviewed first.
    setKeepSelection(rows.reduce((selection, row) => ({
      ...selection,
      [row.id]: row.offerable && row.droppedCount === 0,
    }), {}));
    setKeepError('');
    setKeepPrompt({
      publishedVersion: result.publishedVersion,
      recipientNames,
      blockedRecipientsSentence,
      hasEmailOutputs: emailOutputs.length > 0,
      rows,
    });
  };

  const handleKeepConfirm = async (withEmail) => {
    if (!keepPrompt || keepBusy) return;
    const selected = keepPrompt.rows.filter((row) => row.offerable && keepSelection[row.id]);
    if (selected.length === 0) {
      setKeepPrompt(null);
      return;
    }

    setKeepBusy(true);
    setKeepError('');
    try {
      const outcome = await keepAutomationsRunning(
        companyName,
        functionId,
        selected.map((row) => ({
          id: row.id,
          sweep_token: row.sweepToken,
          expectedConfig: row.expectedConfig,
          hadEmail: row.hadEmail,
        })),
        keepPrompt.publishedVersion,
        (withEmail ? selected : []).filter((row) => row.canRestoreEmail).map((row) => row.id),
        actorUid,
      );
      if (outcome.applied) {
        setMessages(prev => [...prev, {
          text: `${selected.length} automation${selected.length === 1 ? '' : 's'} kept running on version ${keepPrompt.publishedVersion}. ${withEmail ? 'Email was restored only for the eligible automations shown in the review.' : 'Email stays off.'}`,
          type: 'success',
        }]);
      } else {
        // A staleness check failed: nothing was written, and the prompt closes
        // pointing the user at the Automations tab.
        setMessages(prev => [...prev, {
          text: STALE_MESSAGES[outcome.reason] || 'These automations changed since this publish, so nothing was written. Review them in the Automations tab.',
          type: 'status',
        }]);
      }
      setKeepPrompt(null);
    } catch (err) {
      // A genuine write failure keeps the prompt (and its selection) open.
      console.error('Failed to keep automations running:', err);
      setKeepError(err.message || 'Could not keep these automations running.');
    } finally {
      setKeepBusy(false);
    }
  };

  const handleKeepDecline = () => {
    if (keepBusy) return;
    setKeepPrompt(null);
    setKeepError('');
  };

  const getIcon = (type) => {
    if (type === 'error') return <ErrorOutlineIcon color="error" />;
    if (type === 'success') return <CheckCircleOutlineIcon color="success" />;
    return <InfoIcon color="info" />;
  };

  const busy = (isPublishing && !isComplete) || checkingPublish || Boolean(keepPrompt);
  const keepVersion = keepPrompt?.publishedVersion;
  const selectedKeepRows = (keepPrompt?.rows || []).filter((row) => row.offerable && keepSelection[row.id]);
  const hasEmailSelection = selectedKeepRows.some((row) => row.canRestoreEmail);

  return (
    <Dialog open={open} onClose={busy ? undefined : () => onClose(published)} fullWidth maxWidth="md">
      <DialogTitle>Publish Function</DialogTitle>
      <DialogContent dividers>
        {!isPublishing ? (
            <Box textAlign="center" p={3}>
                <Typography variant="h6" gutterBottom>Ready to Publish</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Publishing runs your function end to end to confirm it works, then saves it as the
                    version that every run, view, automation, and agent request will execute. Publishing
                    also switches this function&apos;s automations off until you review them — you&apos;ll
                    be prompted to turn them back on. Each data source reads up to 50,000 matching rows.
                </Typography>
                <Button variant="contained" onClick={handleStartPublish}>
                    Validate &amp; Publish
                </Button>
            </Box>
        ) : (
            <>
                <List dense>
                {messages.map((msg, index) => (
                    <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                        {getIcon(msg.type)}
                    </ListItemIcon>
                    <ListItemText
                        primary={<Typography variant="body2">{msg.text}</Typography>}
                    />
                    </ListItem>
                ))}
                </List>
                {!isComplete && (
                <Box display="flex" alignItems="center" p={2}>
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                    <Typography variant="body2" color="text.secondary">Publishing in progress, please wait...</Typography>
                </Box>
                )}
                {isComplete && (
                    <Alert severity={uncertain ? "warning" : hasError ? "error" : "success"} sx={{ mt: 2 }}>
                        {uncertain
                          ? "The publish result could not be confirmed. Check the published version and the Automations tab before retrying."
                          : hasError
                            ? "Publishing failed, so nothing changed. Review the errors above and edit the function."
                            : "This function is published. Runs will execute the published version."}
                    </Alert>
                )}
            </>
        )}
      </DialogContent>
        <DialogActions>
          {uncertain && (
            <Button onClick={settleFromHead} disabled={busy}>Check again</Button>
          )}
          <Button onClick={() => onClose(published)} disabled={busy}>Close</Button>
        </DialogActions>

      <Dialog open={Boolean(keepPrompt)} onClose={handleKeepDecline} fullWidth maxWidth="sm">
        <DialogTitle>Keep these automations running?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {`This function is used by the following automations, which have been switched off because version ${keepVersion} changes what the function does:`}
            <Box sx={{ mt: 1.5 }}>
              {(keepPrompt?.rows || []).map((row, index) => {
                let unavailableReason = 'This automation needs attention. Review it in the Automations tab.';
                if (row.orphaned && row.tableLabel) {
                  unavailableReason = `Watches ${row.tableLabel}, which version ${keepVersion} no longer reads. Reconfigure it in the Automations tab.`;
                } else if (row.changed) {
                  unavailableReason = 'Changed since this publish. Review it in the Automations tab.';
                } else if (row.unreadable) {
                  unavailableReason = 'The version it ran on could not be read, so its saved filters cannot be checked here. Turn it back on from the Automations tab.';
                }
                let emailEffect = 'Email stays off with either action.';
                if (!keepSelection[row.id]) {
                  emailEffect = 'Will stay off.';
                } else if (row.canRestoreEmail) {
                  emailEffect = `Keep running leaves Email off. Keep running with Email restores sending to version ${keepVersion}'s recipients: ${keepPrompt.recipientNames.join(', ')}.`;
                } else if (row.droppedCount > 0) {
                  emailEffect = 'Email stays off with either action because this version removes saved filters.';
                } else if (row.hadEmail && keepPrompt.hasEmailOutputs && keepPrompt.blockedRecipientsSentence) {
                  emailEffect = `Email stays off with either action because a recipient cannot be emailed. ${keepPrompt.blockedRecipientsSentence}`;
                } else if (row.hadEmail && keepPrompt.hasEmailOutputs) {
                  emailEffect = 'Email stays off with either action because its recipients could not be read.';
                }
                return (
                  <Stack key={row.id || `swept-${index}`} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                    {row.offerable ? (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={Boolean(keepSelection[row.id])}
                        disabled={keepBusy}
                        onChange={(e) => setKeepSelection((prev) => ({ ...prev, [row.id]: e.target.checked }))}
                        inputProps={{ 'aria-label': `Keep ${row.name} running` }}
                      />
                    ) : (
                      <WarningAmberIcon color="warning" fontSize="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="body2" component="div">
                      <strong>{row.name}</strong>
                      {!row.offerable ? (
                        ` — ${unavailableReason}`
                      ) : (
                        <>
                          {` — ${row.when}.`}
                          {row.droppedCount > 0 && (
                            ` ${row.droppedCount} saved filter${row.droppedCount === 1 ? '' : 's'} no longer appl${row.droppedCount === 1 ? 'ies' : 'y'} and will be removed — review before re-enabling.`
                          )}
                          {' '}{emailEffect}
                        </>
                      )}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          </DialogContentText>
          {keepError && <Alert severity="error" sx={{ mt: 2 }}>{keepError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepDecline} disabled={keepBusy}>Leave off</Button>
          <Button onClick={() => handleKeepConfirm(false)} variant="contained" disabled={keepBusy || selectedKeepRows.length === 0}>
            Keep running
          </Button>
          <Button onClick={() => handleKeepConfirm(true)} variant="outlined" disabled={keepBusy || !hasEmailSelection}>
            Keep running with Email
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default PublishFunctionDialog;
